import { useEffect, useRef } from "react";
import client, { DOMA_API_URL } from "../api/ApiClient";

export function useVisitTracking(
  investmentCode: string,
  activeReportCode: string | undefined,
  visitTrackingEnabled: boolean,
  isPreview: boolean = false
) {
  const visitIdRef = useRef<string | null>(null);
  const sectionTimersRef = useRef<Map<string, number>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const observedSectionsRef = useRef<Set<Element>>(new Set());

  // ── 1. Start wizyty i zakończenie przez sendBeacon ───────────────────────
  useEffect(() => {
    if (!visitTrackingEnabled || isPreview || !investmentCode) return;

    const existingId = sessionStorage.getItem("domaVisitId");

    if (existingId) {
      // Odśwież – ta sama sesja w przeglądarce
      visitIdRef.current = existingId;
    } else {
      // Nowa wizyta
      const startVisit = async () => {
        try {
          const res = await client.post("/OfferVisit/start", {
            investmentCode,
            reportNumber: activeReportCode,
            deviceInfo: {
              userAgent: navigator.userAgent,
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              viewportSize: `${window.innerWidth}x${window.innerHeight}`,
              language: navigator.language,
              refererUrl: document.referrer,
            },
          });

          if (res.data?.visitId) {
            visitIdRef.current = res.data.visitId;
            sessionStorage.setItem("domaVisitId", res.data.visitId);
          }
        } catch {
          // Śledzenie nigdy nie może crashować raportu – ignorujemy błędy
        }
      };

      startVisit();
    }

    // Zakończenie wizyty – sendBeacon gwarantuje wysłanie nawet przy zamknięciu karty
    const handleUnload = () => {
      const id = visitIdRef.current;
      if (!id) return;
      const url = `${DOMA_API_URL}/OfferVisit/end/${id}`;
      navigator.sendBeacon(url, new Blob([JSON.stringify({})], { type: "application/json" }));
      sessionStorage.removeItem("domaVisitId");
    };

    window.addEventListener("beforeunload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [visitTrackingEnabled, investmentCode]);

  // ── 2. Śledzenie zmiany wariantu ─────────────────────────────────────────
  const prevReportCodeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!visitTrackingEnabled || isPreview) return;
    if (!activeReportCode) return;
    if (prevReportCodeRef.current === undefined) {
      prevReportCodeRef.current = activeReportCode;
      return;
    }
    if (prevReportCodeRef.current !== activeReportCode) {
      const from = prevReportCodeRef.current;
      prevReportCodeRef.current = activeReportCode;
      sendEvent({
        eventType: "variant_change",
        reportNumber: activeReportCode,
        extraData: JSON.stringify({ from, to: activeReportCode }),
      });
    }
  }, [activeReportCode, visitTrackingEnabled]);

  // ── 3. IntersectionObserver – czas per sekcja ────────────────────────────
  useEffect(() => {
    if (!visitTrackingEnabled || isPreview) return;

    // Odłącz poprzedni observer przy zmianie wariantu
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const name = (entry.target as HTMLElement).dataset.section!;
          if (entry.isIntersecting) {
            if (!sectionTimersRef.current.has(name)) {
              sectionTimersRef.current.set(name, Date.now());
            }
          } else {
            const startTime = sectionTimersRef.current.get(name);
            if (startTime !== undefined) {
              const timeSpentMs = Date.now() - startTime;
              sectionTimersRef.current.delete(name);
              if (timeSpentMs >= 500) {
                sendEvent({
                  eventType: "section_view",
                  sectionName: name,
                  reportNumber: activeReportCode,
                  timeSpentMs,
                });
              }
            }
          }
        });
      },
      { threshold: 0.25 }
    );
    observerRef.current = io;
    observedSectionsRef.current.clear();

    // Obserwuj wszystkie już istniejące sekcje
    const observeNew = (root: Document | Element = document) => {
      root.querySelectorAll<HTMLElement>("[data-section]").forEach((el) => {
        if (!observedSectionsRef.current.has(el)) {
          observedSectionsRef.current.add(el);
          io.observe(el);
        }
      });
    };

    // MutationObserver – wyłapuje sekcje dodawane dynamicznie (np. AutoPages)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.dataset.section) {
              if (!observedSectionsRef.current.has(node)) {
                observedSectionsRef.current.add(node);
                io.observe(node);
              }
            }
            // Sprawdź też potomków dodanego węzła
            node.querySelectorAll<HTMLElement>("[data-section]").forEach((el) => {
              if (!observedSectionsRef.current.has(el)) {
                observedSectionsRef.current.add(el);
                io.observe(el);
              }
            });
          }
        });
      });
    });
    mutationObserverRef.current = mo;
    mo.observe(document.body, { childList: true, subtree: true });

    // Daj czas na pierwsze renderowanie statycznych sekcji, dynamiczne złapie MutationObserver
    const timer = setTimeout(() => observeNew(), 500);

    return () => {
      clearTimeout(timer);
      io.disconnect();
      mo.disconnect();
      sectionTimersRef.current.clear();
      observedSectionsRef.current.clear();
    };
  }, [activeReportCode, visitTrackingEnabled]);

  // ── 4. Śledzenie drukowania ───────────────────────────────────────────────
  useEffect(() => {
    if (!visitTrackingEnabled || isPreview) return;

    const handlePrint = () => {
      sendEvent({ eventType: "print", reportNumber: activeReportCode });
    };

    window.addEventListener("beforeprint", handlePrint);
    return () => window.removeEventListener("beforeprint", handlePrint);
  }, [activeReportCode, visitTrackingEnabled]);

  // ── Helper wysyłający zdarzenie ───────────────────────────────────────────
  const sendEvent = async (data: {
    eventType: string;
    sectionName?: string;
    reportNumber?: string;
    timeSpentMs?: number;
    extraData?: string;
  }) => {
    const id = visitIdRef.current;
    if (!id) return;
    try {
      await client.post("/OfferVisit/event", { visitId: id, ...data });
    } catch {
      // Ignoruj błędy – śledzenie nie może crashować raportu
    }
  };
}
